<template>
    <div class="EnumInputWrapper" :class="{ 'EnumInputWrapper--custom': useCustomValue }">
        <DefaultLayout
            :id="id"
            input-container-class="EnumInput__inputContainer"
            class="EnumInput"
            :class="{ 'EnumInput--error': error, 'EnumInput--disabled': disabled }"
            :label="label"
            :required="required"
            :error="error"
        >
            <template v-if="!useCustomValue">
                <NewDropdown
                    class="EnumInput__dropdown"
                    :options="options"
                    :selected="selected"
                    :disabled="disabled"
                    @input="onDropdownInput"
                >
                    <template #prepend>
                        <slot name="inputInnerPrefix" />
                    </template>
                </NewDropdown>
            </template>
            <template v-if="useCustomValue" #default="{ id }">
                <input
                    :id="id"
                    class="EnumInput__textField"
                    type="text"
                    :value="value"
                    :disabled="disabled"
                    @input="onTextInput"
                >
            </template>
            <template
                v-for="name in dynamicSlotNames"
                #[name]
            >
                <slot :name="name" />
            </template>
        </DefaultLayout>
        <CheckboxInput
            v-if="allowCustomValues"
            class="EnumInputWrapper__useCustomValueCheckbox"
            label="Check if not listed"
            :name="`${id}::useCustomValue`"
            :value="true"
            :model-value="useCustomValue"
            :disabled="disabled"
            @change="onToggleCustomValue"
        />
    </div>
</template>

<style lang="scss">
@import "../style-variables.scss";

$enumInputSelector: ".EnumInput__inputContainer .q-field__control";
$enumInputborderContainerSelector: "#{$enumInputSelector}::before, #{$enumInputSelector}::after";

.EnumInputWrapper {
    .DdrConfidenceLevelBadge {
        svg {
            width: 16px;
            height: 16px;
        }
    }

    .NewDropdown {
        min-width: 200px;

        // align prepend icon with other fields, q prepend default 7px while other fields has gap 14px
        .q-field__prepend {
            padding-right: $input-gap;
        }
    }

    &__useCustomValueCheckbox {
        margin-top: 7px;
    }

    #{$enumInputborderContainerSelector} {
        border: $border;
        border-radius: $border-radius;

        &:hover, &:focus, &:active {
            border: none;
        }
    }

    .EnumInput--error {
        #{$enumInputborderContainerSelector} {
            border-color: var(--eidDangerRed);
        }
    }

    .EnumInput--disabled {
        #{$enumInputSelector} {
            background-color: $background-input-disabled;
        }
        #{$enumInputborderContainerSelector} {
            border-color: $border-color-disabled;
        }
    }

    &--custom {
        .EnumInput__inputContainer {
            @include input-container;
        }

        .EnumInput__textField {
            @include input-field;
        }

        .EnumInput--error {
            @include input-error-state(".EnumInput__inputContainer");
        }

        .EnumInput--disabled {
            @include input-disabled-state(".EnumInput__inputContainer");
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { DropdownOption, NewDropdown } from '@evidentid/dashboard-commons/components/Dropdown';

    import CheckboxInput from '@/modules/dynamic-form/components/style-inputs/checkbox-input/CheckboxInput.vue';
    import { commonInputProps } from '@/modules/dynamic-form/components/style-inputs/common-input-props';
    import DefaultLayout from '@/modules/dynamic-form/components/style-inputs/default-layout/DefaultLayout.vue';
    import { defaultLayoutSlotForwardingMixin } from '@/modules/dynamic-form/components/style-inputs/default-layout-slot-forwarding.mixin';

    export default defineComponent({
        name: 'EnumInput',
        components: { DefaultLayout, NewDropdown, CheckboxInput },
        mixins: [ defaultLayoutSlotForwardingMixin ],
        props: {
            ...commonInputProps,
            value: {
                type: [ String, Number ] as PropType<string | number>,
                default: '',
            },
            options: {
                type: Array as PropType<DropdownOption[]>,
                default: (): DropdownOption[] => [],
            },
            allowCustomValues: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'input' ],
        data() {
            return {
                useCustomValue: false,
            };
        },
        computed: {
            selected(): DropdownOption | null {
                return this.options.find((option) => option.value === this.value) || null;
            },
            dynamicSlotNames(): string[] {
                // when not use custom value, enum Dropdown comp will handle the inputInnerPrefix slot, hence filter out
                const self = this as any;
                const slotNames = self.useCustomValue
                    ? self.DEFAULT_LAYOUT_SLOT_NAMES
                    : self.DEFAULT_LAYOUT_SLOT_NAMES.filter((name: string) => name !== 'inputInnerPrefix');
                return slotNames;
            },
        },
        mounted() {
            // handling the case when value is auto extracted or from auto saved(not yet implemented)
            const hasValue = this.value !== null && this.value !== undefined && this.value !== '';
            const valueInOptions = this.options.some((option) => option.value === this.value);
            this.useCustomValue = this.allowCustomValues && hasValue && !valueInOptions;
        },
        methods: {
            onDropdownInput(option?: DropdownOption): void {
                this.$emit('input', option?.value);
            },
            onTextInput(event: Event): void {
                const target = event.target as HTMLInputElement;
                this.$emit('input', target.value);
            },
            onToggleCustomValue(): void {
                this.useCustomValue = !this.useCustomValue;
                this.$emit('input', undefined);
            },
        },
    });
</script>
